import { defineStore } from 'pinia';
import { useFormStore, useAccountStore } from '@/store';
import {
  IDecisionIntelligenceAnalytics,
  IFormAnalytics,
  IAnalyticsProfileInfo,
  AnalyticsLinkClick,
} from '@/interfaces/forms/analytics-interface';
import { FormEvents } from '@/enums/form-events.enum';

declare global {
  interface Window {
    adobeDataLayer: any[];
  }
}

export const useAnalyticsStore = defineStore('Analytics', () => {
  const formStore = useFormStore();
  const accountStore = useAccountStore();

  /**
   * Push data to adobe data layer
   *
   * @param options
   */
  const pushAnalytics = async (data: IFormAnalytics, appendToStepName?: string) => {
    if (typeof window === 'undefined' || !data?.event) return;

    // Set authentication status
    data.form.authStatus = (await accountStore.isAuthenticated) ? 'Logged in' : 'Not logged in';

    // Set form name if not already provided
    data.form.name ||= formStore.pageTitle; // Ignore sonarqube suggestion of ??= as it will only consider null and undefined (would ignore '')

    populateStepName(data, appendToStepName);

    // Set profile info if identity details exist
    if (accountStore.identityDetails?.d365Id) {
      if (!data.profileInfo) {
        data.profileInfo = {} as IAnalyticsProfileInfo;
      }

      data.profileInfo.contactID = accountStore.identityDetails.d365Id;
      if (!data.profileInfo.memberLevel) data.profileInfo.memberLevel = accountStore.identityDetails.membershipLoyalty;
    }

    // Push data to Adobe Data Layer
    window.adobeDataLayer = window.adobeDataLayer || [];
    window.adobeDataLayer.push(data);
  };

  /**
   * Populate step name
   *
   * @param data
   * @param appendToStepName
   */
  const populateStepName = (data: IFormAnalytics, appendToStepName?: string) => {
    if (!data.form.stepName && data.event != FormEvents.JEOPARDY) {
      if (data.event === FormEvents.START) {
        data.form.stepName = 'FormStarted';
      } else if (data.event === FormEvents.PREMIUM_CHANGE) {
        data.form.stepName = 'Premium change';
      } else if (data.event === FormEvents.COMPLETE) {
        data.form.stepName = 'FormCompleted';
      } else if (formStore.currentStep?.stepName) {
        data.form.stepName = appendToStepName
          ? `${formStore.currentStep?.stepName}-${appendToStepName}`
          : formStore.currentStep?.stepName;
      }
    }
  };

  /**
   * Push data to adobe data layer
   *
   * @param options
   */
  const pushDecisionIntelligenceAnalytics = (data: IDecisionIntelligenceAnalytics) => {
    if (typeof window === 'undefined') return;
    if (!data?.event) return;

    window.adobeDataLayer = window.adobeDataLayer || [];
    window.adobeDataLayer.push(data);
  };

  /**
   * Pushes a click event to the adobeDataLayer.
   * @param data The event data to push to the adobeDataLayer
   */
  const pushClickEvent = (data: AnalyticsLinkClick) => {
    pushToAdobeDataLayer(data as any);
  };

  const pushToAdobeDataLayer = (data: any) => {
    if (typeof window === 'undefined') return;
    window.adobeDataLayer = window.adobeDataLayer || [];
    window.adobeDataLayer.push(data);
  };

  return {
    pushAnalytics,
    pushDecisionIntelligenceAnalytics,
    pushClickEvent,
  };
});
